import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { clearTenantScopedCaches } from '../utils/offlineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(
    () => localStorage.getItem('selectedTenantId') || ''
  );
  const [inTenantContext, setInTenantContext] = useState(
    () => localStorage.getItem('inTenantContext') === 'true'
  );

  // Build headers with auth + tenant
  const getHeaders = useCallback((extra = {}) => {
    const token = localStorage.getItem('token');
    const tid = localStorage.getItem('selectedTenantId');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (tid) headers['X-Tenant-Id'] = tid;
    return { ...headers, ...extra };
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Load tenants when user is master
  useEffect(() => {
    if (user?.is_master) {
      loadTenants();
    }
  }, [user?.is_master]);

  // Set default selectedTenantId from user on first login
  useEffect(() => {
    if (user && !selectedTenantId && user.tenant_id) {
      setSelectedTenantId(user.tenant_id);
      localStorage.setItem('selectedTenantId', user.tenant_id);
    }
  }, [user, selectedTenantId]);

  const checkAuth = async () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.user_id && !userData.id) {
          userData.id = userData.user_id;
        }
        setUser(userData);
        setLoading(false);
        // Revalida em background se houver conexão (não bloqueia a UI offline)
        if (navigator.onLine) {
          revalidateSession();
        }
        return;
      } catch (e) {
        localStorage.removeItem('user');
      }
    }

    const token = localStorage.getItem('token');

    // OFFLINE: sem rede, não tenta /api/auth/me (caso contrário, o catch limparia o token).
    // Se tem token mas o objeto user nunca foi persistido, espera voltar online no próximo evento.
    if (!navigator.onLine) {
      setLoading(false);
      return;
    }

    if (!token) {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setLoading(false);
          return;
        }
      } catch (error) {
        // No session
      }
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else if (response.status === 401 || response.status === 403) {
        // APENAS 401/403 = sessão realmente inválida -> limpa
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedTenantId');
      }
      // Outros status (5xx, 503 offline-mode do SW) NÃO limpam credenciais
    } catch (error) {
      // Erro de rede (offline, DNS, timeout) — NÃO limpa credenciais.
      // O operador continua usando o app com os dados em cache.
      console.warn('[Auth] checkAuth network error, mantendo sessão local:', error?.message);
    }
    setLoading(false);
  };

  // Revalidação em background — atualiza o user em cache se o servidor responder OK.
  // Em caso de 401/403, faz logout. Em caso de erro de rede, mantém sessão.
  const revalidateSession = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedTenantId');
        setUser(null);
      }
    } catch (_) {
      // offline / rede instável — mantém sessão local
    }
  };

  const loadTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tenants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenants(data);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  };

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userName', userData.name || '');
    // Persistir o objeto user completo para suportar cold boot offline (PWA / Android 16)
    try {
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (e) {
      console.warn('[Auth] Falha ao persistir user no localStorage:', e);
    }
    // Master admin: não pré-selecionar tenant (vai para Visão Geral)
    if (!userData.is_master && userData.tenant_id) {
      localStorage.setItem('selectedTenantId', userData.tenant_id);
      setSelectedTenantId(userData.tenant_id);
    }
    setUser(userData);

    // Solicita storage persistente — Android 16 / Chromium não evictará IndexedDB/localStorage
    // do PWA por pressão de memória ou bateria depois disso.
    // Best-effort: alguns browsers só concedem após heurísticas (instalação como PWA, engajamento).
    if (navigator.storage && typeof navigator.storage.persist === 'function') {
      navigator.storage.persist()
        .then((granted) => {
          console.log(`[Auth] storage.persist() => ${granted ? 'granted' : 'not granted'}`);
        })
        .catch((err) => {
          console.warn('[Auth] storage.persist() falhou:', err);
        });
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/google/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      // Ignore
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedTenantId');
    localStorage.removeItem('inTenantContext');
    setUser(null);
    setSelectedTenantId('');
    setInTenantContext(false);
    setTenants([]);
  };

  const switchTenant = (tenantId) => {
    localStorage.setItem('selectedTenantId', tenantId);
    setSelectedTenantId(tenantId);
    clearTenantScopedCaches().catch(() => {});
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      toast.success(`Empresa alterada para: ${tenant.name}`);
    }
  };

  const enterTenant = (tenantId) => {
    localStorage.setItem('selectedTenantId', tenantId);
    localStorage.setItem('inTenantContext', 'true');
    setSelectedTenantId(tenantId);
    setInTenantContext(true);
    clearTenantScopedCaches().catch(() => {});
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      toast.success(`Acessando: ${tenant.name}`);
    }
  };

  const exitTenantContext = () => {
    localStorage.removeItem('inTenantContext');
    setInTenantContext(false);
  };

  const currentTenantName = tenants.find(t => t.id === selectedTenantId)?.name || user?.tenant_name || '';

  // Derived permission helpers
  const isMasterAuxiliar = user?.is_master_auxiliar === true;
  const permissions = user?.permissions || null;

  const hasPermission = useCallback((module, action) => {
    if (!isMasterAuxiliar) return true; // Owner master or tenant users have full access by role
    if (!permissions || !permissions[module]) return false;
    return permissions[module][action] === true;
  }, [isMasterAuxiliar, permissions]);

  const value = {
    user,
    loading,
    tenants,
    selectedTenantId,
    currentTenantName,
    inTenantContext,
    getHeaders,
    login,
    logout,
    switchTenant,
    enterTenant,
    exitTenantContext,
    isMasterAuxiliar,
    permissions,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
